import { ApiError, type AuthSchemas, type CharacterSchemas } from '@ddt/api-client';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getServices } from '../services';
import { useMail } from './mail';

export type Account = AuthSchemas['AccountResponse'];
export type CharacterInfo = CharacterSchemas['CharacterResponse'];
export type AttributeKey = 'strength' | 'dexterity' | 'constitution' | 'intelligence' | 'wisdom' | 'charisma';

/** Valores numéricos da API chegam como `number | string` (quirk do gerador de OpenAPI do .NET): normaliza. */
export const num = (value: number | string | null | undefined): number => Number(value ?? 0);

export const useSession = defineStore('session', () => {
  const account = ref<Account | null>(null);
  const character = ref<CharacterInfo | null>(null);
  const ready = ref(false);

  const authenticated = computed(() => account.value !== null);
  const roles = computed(() => account.value?.roles ?? []);
  const isGm = computed(() => roles.value.includes('gm') || roles.value.includes('admin'));
  const isAdmin = computed(() => roles.value.includes('admin'));

  async function loadProfile(): Promise<void> {
    const { api } = getServices();
    const me = await api.auth.GET('/v1/accounts/me');
    if (!me.data) throw new ApiError(me.response.status, 'profile', 'Não foi possível carregar a conta.');
    account.value = me.data;
    await refreshCharacter();
  }

  /** O personagem é criado por evento logo após o cadastro: pode levar um instante para existir. */
  async function refreshCharacter(attempts = 6): Promise<void> {
    const { api } = getServices();
    for (let i = 0; i < attempts; i++) {
      const result = await api.character.GET('/v1/characters/me');
      if (result.data) {
        character.value = result.data;
        return;
      }
      if (result.response.status !== 404) return;
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  /** O personagem só existe de fato depois que o jogador escolhe nickname e sexo. */
  const needsCharacter = computed(() => authenticated.value && character.value !== null && !character.value.isCreated);

  async function checkNickname(nickName: string): Promise<CharacterSchemas['NicknameAvailabilityResponse']> {
    const { api } = getServices();
    const result = await api.character.GET('/v1/characters/nickname-availability', { params: { query: { nickName } } });
    if (!result.data) throw new ApiError(result.response.status, 'nickname', 'Não foi possível verificar o nickname.');
    return result.data;
  }

  async function createCharacter(nickName: string, sex: boolean): Promise<void> {
    const { api } = getServices();
    const result = await api.character.POST('/v1/characters', { body: { nickName, sex } });
    if (!result.data) {
      const problem = result.error as { code?: string; title?: string; detail?: string } | undefined;
      throw new ApiError(result.response.status, problem?.code ?? 'create_character', problem?.detail ?? problem?.title ?? 'Não foi possível criar o personagem.');
    }
    character.value = result.data;
  }

  /** Distribui pontos livres (valores a somar a cada atributo). O servidor valida o saldo e devolve o personagem atualizado. */
  async function allocateAttributes(points: Record<AttributeKey, number>): Promise<void> {
    const { api } = getServices();
    const result = await api.character.POST('/v1/characters/me/attributes', { body: points });
    if (!result.data) {
      const problem = result.error as { code?: string; title?: string; detail?: string } | undefined;
      throw new ApiError(result.response.status, problem?.code ?? 'allocate_attributes', problem?.detail ?? problem?.title ?? 'Não foi possível distribuir os pontos.');
    }
    character.value = result.data;
  }

  /** Ao abrir o app: se há sessão guardada, carrega o perfil; se ela não vale mais, limpa. */
  async function restore(): Promise<void> {
    const { api } = getServices();
    try {
      if (api.tokens.get()) await loadProfile();
    } catch {
      clear();
    } finally {
      ready.value = true;
    }
  }

  async function login(login: string, password: string): Promise<void> {
    const { api } = getServices();
    await api.login(login, password);
    await loadProfile();
  }

  async function register(username: string, email: string, password: string): Promise<void> {
    const { api } = getServices();
    await api.register(username, email, password);
    await loadProfile();
  }

  async function logout(): Promise<void> {
    const { api } = getServices();
    await api.logout();
    clear();
  }

  function clear(): void {
    getServices().api.tokens.set(null);
    account.value = null;
    character.value = null;
    useMail().clear();
  }

  return { account, character, ready, authenticated, needsCharacter, checkNickname, createCharacter, allocateAttributes, roles, isGm, isAdmin, restore, login, register, logout, refreshCharacter, loadProfile, clear };
});
